import { Outlet, Link, NavLink, Navigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
    LayoutDashboard, CalendarDays, ScanLine, Users, Building2, UsersRound,
    Settings2, ArrowLeft, Menu, X, ChevronDown, Check, FileText,
} from "lucide-react";
import { useAuth } from "@/lib/authContext";
import { Wordmark } from "@/components/Wordmark";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
    { path: "overview", label: "Overview", icon: LayoutDashboard, roles: ["manager", "staff", "admin"] },
    { path: "events", label: "Events", icon: CalendarDays, roles: ["manager", "staff", "admin"] },
    { path: "templates", label: "Templates", icon: FileText, roles: ["manager", "admin"] },
    { path: "check-in", label: "Check-In", icon: ScanLine, roles: ["manager", "staff", "admin"] },
    { path: "audience", label: "Audience", icon: Users, roles: ["manager", "admin"] },
    { path: "profile", label: "Venue Page", icon: Building2, roles: ["manager", "admin"] },
    { path: "team", label: "Team", icon: UsersRound, roles: ["manager", "admin"] },
    { path: "settings", label: "Settings", icon: Settings2, roles: ["manager", "admin"] },
];

export default function VenueLayout() {
    const { venues = [], user, logout } = useAuth();
    const { venueId } = useParams();
    const [open, setOpen] = useState(false);

    const activeVenue = useMemo(() => venues.find((v) => v.id === venueId) || venues[0] || null, [venues, venueId]);

    // If no venue in URL and user has a venue, redirect to first one's overview
    if (!venueId && activeVenue) {
        return <Navigate to={`/venue/${activeVenue.id}/overview`} replace />;
    }
    if (!activeVenue) {
        return (
            <div className="p-8 md:p-16" data-testid="venue-empty">
                <div className="border border-[#2a2a2a] p-8 max-w-2xl">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">No venue access</div>
                    <h1 className="mt-2 font-display font-black uppercase text-3xl">You aren't a manager for any venue yet</h1>
                    <p className="mt-3 text-sm text-[#a3a3a3]">Ask a Platform Admin to assign you, or open the invitation link you received.</p>
                    <Link to="/" className="pm-btn pm-btn-primary mt-6 inline-flex" data-testid="venue-empty-home">Back to home</Link>
                </div>
            </div>
        );
    }

    const role = activeVenue.role;
    const items = NAV.filter((it) => it.roles.includes(role));

    return (
        <div className="relative min-h-screen flex flex-col md:flex-row" data-testid="venue-layout">
            <div className="md:hidden sticky top-0 z-40 bg-[#0a0a0a] border-b border-[#2a2a2a] px-4 h-14 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => setOpen(!open)} data-testid="venue-mobile-toggle" className="p-2 border border-[#2a2a2a]">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    <Wordmark small />
                </div>
                <div className="text-xs font-mono text-[#a3a3a3] uppercase">Venue</div>
            </div>

            <aside
                className={`bg-[#0a0a0a] border-r border-[#2a2a2a] w-full md:w-[240px] md:fixed md:top-0 md:bottom-0 md:left-0 md:z-40 md:flex md:flex-col ${open ? "block" : "hidden md:block"}`}
                data-testid="venue-sidebar"
            >
                <div className="hidden md:flex items-center h-16 px-6 border-b border-[#2a2a2a]">
                    <Link to="/" className="flex items-center gap-2"><Wordmark small /></Link>
                </div>
                <div className="px-6 py-4 border-b border-[#2a2a2a]">
                    <div className="text-[10px] uppercase tracking-widest text-[#666] font-mono">Active Venue</div>
                    {venues.length > 1 ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger className="w-full mt-1 flex items-center justify-between text-left group" data-testid="venue-switcher">
                                <span className="font-display font-bold text-lg leading-tight truncate">{activeVenue.name}</span>
                                <ChevronDown className="w-4 h-4 text-[#666] group-hover:text-[#ff4400]" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#141414] border-[#2a2a2a] rounded-none text-[#f4f4f0] w-64">
                                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-[#666]">Switch venue</DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                                {venues.map((v) => (
                                    <DropdownMenuItem key={v.id} asChild className="cursor-pointer rounded-none focus:bg-[#1f1f1f] focus:text-[#ff4400]">
                                        <Link to={`/venue/${v.id}/overview`} data-testid={`venue-option-${v.id}`}>
                                            <span className="flex-1 font-display font-bold uppercase tracking-wide text-sm truncate">{v.name}</span>
                                            {v.id === activeVenue.id && <Check className="w-4 h-4 text-[#ff4400]" />}
                                        </Link>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div className="mt-1 font-display font-bold text-lg leading-tight" data-testid="venue-name">{activeVenue.name}</div>
                    )}
                    <div className="mt-1 text-[10px] font-mono uppercase text-[#a3a3a3]">Role: {role}</div>
                </div>

                <nav className="flex-1 py-3">
                    {items.map((it) => {
                        const Icon = it.icon;
                        return (
                            <NavLink
                                key={it.path}
                                to={`/venue/${activeVenue.id}/${it.path}`}
                                onClick={() => setOpen(false)}
                                data-testid={`venue-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-6 py-3 text-sm font-display font-bold uppercase tracking-wider border-l-2 transition-colors ${
                                        isActive ? "border-[#ff4400] text-[#ff4400] bg-[#141414]" : "border-transparent text-[#f4f4f0] hover:text-[#ff4400] hover:bg-[#141414]"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" /> {it.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="border-t border-[#2a2a2a] px-6 py-4 space-y-3">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Signed in</div>
                    <div className="text-xs font-mono truncate" data-testid="venue-user-email">{user?.email}</div>
                    <div className="flex items-center gap-2 pt-2 min-w-0">
                        <Link to="/account" className="pm-btn text-xs flex-1 whitespace-nowrap min-w-0 justify-center" data-testid="venue-account-link">Account</Link>
                        <button onClick={logout} className="pm-btn text-xs whitespace-nowrap" data-testid="venue-logout-btn">Log out</button>
                    </div>
                    <Link to="/" className="mt-1 flex items-center gap-2 text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400] whitespace-nowrap min-w-0" data-testid="venue-back-public">
                        <ArrowLeft className="w-3 h-3 shrink-0" /> <span className="truncate">Back to public site</span>
                    </Link>
                </div>
            </aside>

            <main className="flex-1 relative z-10 min-w-0 md:ml-[240px]">
                <Outlet context={{ venue: activeVenue, role }} />
            </main>
        </div>
    );
}

export function useVenueOutlet() {
    // useOutletContext from react-router-dom
    // Actual import happens in children to avoid circular deps.
    return null;
}
